import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, SimpleChanges } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { ConfirmationService, MessageService, TreeNode } from "primeng/api";
import { AccountModuleId, CaseModuleId, LeadModuleId, workspaceModuleId } from "src/app/modules/shared/constants/modules.constant";
import { ParseHelper } from "src/app/modules/shared/parse.helper";
import { AppMainService } from "src/app/app-main.service";
import { ImportService } from "src/app/modules/shared/services/import.service";
import { AddWorkbookToAccountPayload } from "../models/workbook-account-mapping.model";

interface DropdownListM { id: number; name: string; }

@Component({
    selector: "app-wb-module-object-field-mapping",
    styleUrls: ["./wb-module-object-field-mapping.component.scss"],
    templateUrl: "./wb-module-object-field-mapping.component.html",
})
export class WbModuleObjectFieldMappingComponent implements OnInit, OnDestroy {

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() displayFieldMapping: boolean = false;
    @Input() selectedModule: DropdownListM;
    @Input() selectedChannel: DropdownListM;
    @Input() sectionColumns: any[] = [];
    @Input() selectedAction: { name: string, value: string };
    @Input() selectedFile: File | null = null;
    @Input() workspaceId: string = "";
    @Input() isCopyMoveWBRows: boolean;
    @Input() selectedFileHeaders: {
        excelHeader: string;
        excelColumn: string;
        columnNumber: number | string;
        moduleField: any;
        moduleFields: any[];
        dataType: string;
    }[] = [];
    @Input() importFileWorkbookRows: any[] = [];
    @Input() workbookIntId: number = 0;
    @Output() dialogClosed = new EventEmitter();
    @Output() importStarted = new EventEmitter();
    selectedFileHeadersList: {
        excelHeader: string;
        excelColumn: string;
        columnNumber: number | string;
        moduleField: any;
    }[] = [];
    selectedFileHeadersPrev: {
        excelHeader: string;
        excelColumn: string;
        columnNumber: number | string;
        moduleField: any;
    }[] = [];
    dialogHeader: string = "Select Columns";
    moduleColumns: any[] = [];
    selectableFields: any[] = [];
    requiredFields: any[] = [];
    selectedTabIndex: number = 0;
    formSubmitted: boolean = false;
    displaySummary: boolean = false;

    displayTemplateFields: boolean = false;
    templateSectionColumns: any[] = [];
    contactPrimaryField: string = "contactSequenceNumber";
    addressPrimaryField: string = "addressTypeId";
    contactFields: string[] = [];
    addressFields: string[] = [];

    /**Refrence Column */
    refrenceColumn: any = null;
    referenceError: string | null = null;
    workspaceModuleName: string = "Workspace";
    referenceColumnOptions: any[] = [];

    //modulesID
    accountModuleId: number = AccountModuleId;
    leadModuleId: number = LeadModuleId;
    workSpaceModuleId: number = workspaceModuleId;
    treeData: TreeNode[] = [];
    rowIndex: number = 0;
    selectedNodes: any[] = [];


    //treetable filter
    globalFilterValue: string = '';
    tempSelectedNodes: { [key: string]: any[] } = {};

    constructor(
        private messageService: MessageService,
        private appMainService: AppMainService,
        private importService: ImportService,
        private confirmationService: ConfirmationService
    ) { }

    ngOnInit(): void {
        const addedFilteHeaders = this.selectedFileHeaders.map(fileHeader => {
            return {
                ...fileHeader,
                moduleField: null
            }
        });
        this.selectedFileHeadersList = ParseHelper.DeepCopy(addedFilteHeaders);
        this.generateModuleColumnsTree(this.sectionColumns);
    }

    ngOnChanges(changes: SimpleChanges): void {
        
        if (changes['sectionColumns']) {
                this.sectionColumns = changes['sectionColumns'].currentValue;
                this.unmapAllFieldsResolver();
                if(this.sectionColumns.length > 0) {
                    this.generateModuleColumnsTree(this.sectionColumns);
                }
        }
    }

    //#region import module selection

    generateModuleColumnsTree(moduleColumnList: any[]) {
        const selectableFields: any[] = [];
        const treeData: any[] = [];

        let hasUnmapped = false;

        const filterDDTree = moduleColumnList.map((item, moduleIndex) => {
            const fields = item.fields
                .filter(field => field.ischecked)
                .map((field) => {
                    const childFields = (field?.childFields || [])
                        .filter(childField => childField.ischecked)
                        .map((childField) => {
                            const rField = {
                                label: childField.labelName,
                                parentLabelName: field?.labelName || field?.name,
                                id: childField.id,
                                isSelectableField: true,
                                ...childField,
                                children: []
                            };

                            selectableFields.push({ ...rField, children: null });

                            if (!childField.sectionId) {
                                hasUnmapped = true;
                            }

                            return rField;
                        });

                    const rField = {
                        label: field.labelName,
                        id: field.id,
                        isSelectableField: childFields.length === 0,
                        ...field,
                        children: childFields
                    };

                    if (rField.isSelectableField) {
                        selectableFields.push({ ...rField, children: null });

                        if (!field.sectionId) {
                            hasUnmapped = true;
                        }
                    }

                    return rField;
                });

            const sortedChildren = [...fields].sort((a, b) => a.label.localeCompare(b.label));

            // Add to tree only if there are children
            if (sortedChildren.length > 0) {
                const children = sortedChildren.map((field) => {
                    const childNodes = field.children?.map((childField) => ({
                        key: `${moduleIndex + 1}-${field.id}-${childField.id}`,
                        label: childField.label,
                        data: { id: childField.id, name: childField.name, label: childField.label, ...childField },
                        leaf: !childField.children?.length,
                        selectable: !childField?.children?.length,
                        children: childField.children?.map((grandChild) => ({
                            key: `${moduleIndex + 1}-${field.id}-${childField.id}-${grandChild.id}`,
                            label: grandChild.label,
                            data: { id: grandChild.id, name: grandChild.name, label: grandChild.label, ...grandChild },
                            leaf: true,
                            selectable: !grandChild.children?.length,
                        })) || []
                    })) || [];

                    return {
                        key: `${moduleIndex + 1}-${field.id}`,
                        label: field.label,
                        data: { id: field.id, name: field.name, label: field.label, ...field },
                        leaf: childNodes.length === 0,
                        selectable: !childNodes.length,
                        children: childNodes
                    };
                });

                const treeNode: any = {
                    key: `${moduleIndex + 1}`,
                    label: item.name,
                    data: { label: item.name, id: item?.id, name: item?.name, ...item },
                    selectable: !children.length,
                    expanded: children.length,
                    leaf: item.children?.length === 0,
                };

                if (children.length) {
                    treeNode.children = children;
                }

                treeData.push(treeNode);
            }

            return {
                label: item.name,
                id: item?.id,
                isSelectableField: true,
                children: sortedChildren
            };
        });

        // Add 'Unmapped' node if any fields have no sectionId
        // if (hasUnmapped) {
        //     treeData.unshift({
        //         key: '0',
        //         label: 'Unmapped',
        //         data: { label: 'Unmapped', id: 0 },
        //         leaf: true,
        //         selectable: true
        //     });
        // }

        this.selectableFields = ParseHelper.DeepCopy(selectableFields);
        this.requiredFields = this.selectableFields.filter(field => field.isRequired);
        this.moduleColumns = ParseHelper.DeepCopy(filterDDTree).filter(section => section.children?.length > 0);

        if (hasUnmapped) {
            this.moduleColumns = [ ...this.moduleColumns];
        }

        this.contactFields = this.getSectionColumns(this.contactPrimaryField);
        this.addressFields = this.getSectionColumns(this.addressPrimaryField);
        this.treeData = treeData;
    }
    getSectionColumns(fieldName: string) {
        const contactSections = this.moduleColumns.find(section =>
            (section.children || []).some(field => field.field === fieldName)
        );

        if (!contactSections?.children?.length) return [];

        return contactSections.children.flatMap(field =>
            field.children?.length ? field.children.map(child => child.field) : [field.field]
        );
    }

    setupAutoFieldMapping() {
        this.selectedFileHeaders.forEach((fileHeader) => {
            this.selectedNodes[fileHeader.excelHeader] = [];
            this.selectNodes(this.treeData, this.selectedNodes[fileHeader.excelHeader], fileHeader);
            const selectedFields = this.selectedNodes[fileHeader.excelHeader]
                .filter(node => node.selectable) // Ensure the node is selectable
                .map(node => {
                    const { children, ...data } = node.data;
                    return data;
                });
            fileHeader.moduleFields = selectedFields;
        });
    }

    selectNodes(tree: TreeNode[], checkedNodes: TreeNode[], fileHeader: any) {
        for (const node of tree) {
            const field = node.data;
            const isLabelMatch = field?.labelName?.trim() === fileHeader.excelHeader?.trim() || `${field?.labelName} *` === fileHeader.excelHeader;
            const isFormControlMatch = field?.formControl === fileHeader.excelColumn;
            if ((!this.isCopyMoveWBRows && isLabelMatch) || (this.isCopyMoveWBRows && isFormControlMatch && isLabelMatch)) {
                checkedNodes.push(node);
            }

            if (node.children){
                this.selectNodes(node.children, checkedNodes, fileHeader)
            }
        }
    }

    unmapAllFields() {
        this.confirmationService.confirm({
            message: 'Do you want to unmap all fields?',
            header: 'Unmap Fields Confirmation',
            icon: 'pi pi-exclamation-triangle',
            key: 'workbookdialog',
            accept: () => {
            this.unmapAllFieldsResolver();
            },
            reject: () => { }
        });
    }

    unmapAllFieldsResolver() {
        this.selectedFileHeaders.forEach(fileHeader => {
            fileHeader.moduleField = null;
            fileHeader.moduleFields = [];
            this.selectedNodes = [];
        });
        this.selectedFileHeadersPrev = ParseHelper.DeepCopy(this.selectedFileHeaders);
        this.requiredFields.forEach(field => {
            field.fileHeader = null;
            field.moduleFields = null;
        });
    }

    treeTableVisibleIndex: { [key: number]: boolean } = {}; // Tracks which row's TreeTable is open

    toggleTreeTable(rowIndex: number, field: any): void {
        this.globalFilterValue = '';
        this.rowIndex = rowIndex;
        this.tempSelectedNodes[field.excelHeader] = [...(this.selectedNodes[field.excelHeader] || [])];
        this.treeTableVisibleIndex[rowIndex] = true;
    }

    applyTreetableFilter(treeTable:any, value:string){
        this.globalFilterValue=value;
        treeTable.filterGlobal(this.globalFilterValue, 'contains')
    }

    importData() {
        const mappingFields = this.selectedFileHeaders
            .filter(fileHeader => Array.isArray(fileHeader.moduleFields) && fileHeader.moduleFields.length)
            .flatMap(fileHeader =>
            fileHeader.moduleFields.map(moduleField => {
                return {
                fileHeader: fileHeader.excelHeader,
                columnNumber: fileHeader.columnNumber,
                labelName: moduleField.labelName,
                field: moduleField.field,
                custom: moduleField.custom,
                formControlId: moduleField.id,
                IsRequired: moduleField.isRequired
                };
            })
            );

        // Check if at least one object contains moduleFields
        const hasMappedFields = this.selectedFileHeaders.some(fileHeader => Array.isArray(fileHeader.moduleFields) && fileHeader.moduleFields.length > 0);

        // Check if all required fields are mapped
        const unmappedRequiredFields = this.requiredFields.filter(field =>
            !this.selectedFileHeaders.some(fileHeader =>
            fileHeader.moduleFields?.some(moduleField => moduleField.field === field.field)
            )
        );

        if (!hasMappedFields) {
            this.messageService.add({
            severity: "error", 
            summary: "Mapping Error",
            detail: `At least one ${this.selectedModule?.name?.toLowerCase()} field must be mapped with workbook fields.`,
            });
            return;
        }

        if (unmappedRequiredFields.length > 0) {
            const unmappedFieldNames = unmappedRequiredFields.map(field => field.labelName || field.field).join(", ");
            this.messageService.add({
            severity: "error",
            summary: "Mapping Error",
            detail: `All required fields must be mapped before proceeding. Unmapped fields name: ${unmappedFieldNames}`,
            });
            return;
        }
            let importFileWorkbookRows=[];
        if (this.importFileWorkbookRows && this.importFileWorkbookRows.length > 0) {
            importFileWorkbookRows = this.importFileWorkbookRows.map(row => ({
            rowNumber: row.row.rowNumber,
            copyComment: row.copyComment,
            copyDocument: this.selectedModule?.id==CaseModuleId ? false : row.copyDocument
            }));
        }
        let channelId = this.selectedChannel;
        let payLoad:AddWorkbookToAccountPayload = {
            actionType: this.selectedAction?.value || null,
            mappingFields: JSON.stringify(mappingFields),
            objectName: this.selectedModule?.name,
            objectId: this.selectedModule?.id,
            channelId: channelId,
            WorkspaceId: this.workspaceId,
            WorkbookId: this.workbookIntId.toString(),
            SourceId: this.workbookIntId,
            SourceType: "Workbook",
            ImportFileWorkbookRows: importFileWorkbookRows,
            TargetType: this.selectedModule?.id==AccountModuleId ? "Account" : this.selectedModule?.id==LeadModuleId ? "Lead" : "Case",
        };

        this.appMainService.isShowSpinner("addImportFile");

        this.importService.copyWorkbookToOtherModule(payLoad).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
            next: (response) => {
                this.appMainService.isHideSpinner("addImportFile");
                this.messageService.add({
                    severity: "success",
                    summary: "Success",
                    detail: "File has uploaded successfully...",
                });
                this.importStarted.emit();
                this.closeDialog();
            },
            error: (error) => {
                this.appMainService.isHideSpinner("addImportFile");
                this.messageService.add({
                    severity: "error",
                    summary: "Error",
                    detail: error?.error?.Message || 'Failed to import data, please try again.'
                });
            }
        })
    }

    saveNodeSelection(field: any) {
        const selected = this.tempSelectedNodes[field.excelHeader] || [];
        this.selectedNodes[field.excelHeader] = [...selected];

        const selectedFields = selected.map(node => {
            const { children, ...data } = node.data;
            return data;
        });

        const fileHeader = this.selectedFileHeaders.find(fh => fh.excelHeader === field.excelHeader);
        if (fileHeader) {
            fileHeader.moduleFields = selectedFields;
        }
    }

    removeNode(field: any, item: any): void {
        const selectedNodes = this.selectedNodes[field.excelHeader] || [];
        const nodeIndex = selectedNodes.findIndex((node: TreeNode) => node.data.id === item.id);

        if (nodeIndex !== -1) {
            selectedNodes.splice(nodeIndex, 1);
            this.selectedNodes[field.excelHeader] = [...selectedNodes];

            const fileHeader = this.selectedFileHeaders.find(fh => fh.excelHeader === field.excelHeader);
            if (fileHeader) {
                fileHeader.moduleFields = selectedNodes.map(node => {
                    const { children, ...data } = node.data;
                    return data;
                });
            }
        }
    }
    onNodeSelect(rowNode: any, currentHeaderKey: string): void {
        const selectedId = rowNode?.node?.data?.id;
        const isMappedElsewhere = Object.entries(this.selectedNodes).some(([key, selectionArray]) =>
            key !== currentHeaderKey && selectionArray?.some((node: any) => node?.data?.id === selectedId)
        );

        if (isMappedElsewhere) {
            this.messageService.add({
                severity: "warn",
                summary: "Field Already Mapped",
                detail: "This field is already mapped to another workbook field. Each field can only be mapped to one workbook field.",
            });

            // Remove the node from tempSelectedNodes for the current header
            this.tempSelectedNodes[currentHeaderKey] = this.tempSelectedNodes[currentHeaderKey]?.filter(
                (node: any) => node?.data?.id !== selectedId
            );
            setTimeout(() => {
                this.tempSelectedNodes = structuredClone(this.tempSelectedNodes);
            });
        }   
    }

    //#endregion

    get mappedFieldsCount(): number {
        return this.selectedFileHeaders.filter(fileHeader => fileHeader.moduleFields?.length > 0).length;
    }

    get unMappedFieldsCount(): number {
        return this.selectedFileHeaders.filter(fileHeader => !fileHeader.moduleFields || fileHeader.moduleFields.length === 0).length;
    }

    get unMappedRequiredFields() {
        return this.requiredFields.filter(field => !field.fileHeader)?.length;
    }

    applyAutoMapping() {
        this.confirmationService.confirm({
            message: 'Do you want to auto map the fields?',
            header: 'Auto Mapping Confirmation',
            icon: 'pi pi-info-circle',
            key: 'workbookdialog',
            accept: () => {
                this.setupAutoFieldMapping();
            },
            reject: () => { }
        });
    }

    loadTemplateFields() {
        this.templateSectionColumns = ParseHelper.DeepCopy(this.sectionColumns);
        this.displayTemplateFields = true;
    }

    templateFieldsRefresh(sectionColumns: any) {
        this.sectionColumns = ParseHelper.DeepCopy(sectionColumns);
        this.generateModuleColumnsTree(this.sectionColumns);
        this.selectedFileHeaders.map(fileHeader => {
            if (fileHeader) {
            // Filter out moduleFields that are no longer valid
            fileHeader.moduleFields = fileHeader.moduleFields?.filter(moduleField => {
                const checkField = this.selectableFields.find(field => field.field == moduleField?.field);
                return !!checkField;
            }) || [];
            // Update selectedNodes to remove invalid nodes
            if (this.selectedNodes[fileHeader.excelHeader]) {
                this.selectedNodes[fileHeader.excelHeader] = this.selectedNodes[fileHeader.excelHeader].filter(node => {
                const checkField = this.selectableFields.find(field => field.field == node?.data?.field);
                return !!checkField;
                });
            }

            // If no valid moduleFields remain, reset the moduleFields and nodes
            if (fileHeader.moduleFields.length === 0) {
                fileHeader.moduleField = null;
                this.selectedNodes[fileHeader.excelHeader] = [];
            }
            }
        });

        const addedFilteHeaders = this.selectedFileHeaders.map(fileHeader => {
            if (fileHeader) {
            return {
                ...fileHeader,
                moduleField: null
            };
            }
            return null;
        }).filter(fileHeader => fileHeader !== null);

        this.selectedFileHeadersList = ParseHelper.DeepCopy(addedFilteHeaders);
        this.closeTemplateFields();
    }

    closeTemplateFields() {
        this.displayTemplateFields = false;
    }

    closeDialog() {
        this.displayFieldMapping = false;
        this.dialogClosed.emit(true);
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }
}
